import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Clock3, Loader2, Plus, Search } from "lucide-react";
import { ShieldUser } from "@/components/shell/icons";
import { useAuthToken } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useEngagement } from "@/hooks/useEngagement";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { PlanGate, UpgradeModal } from "@/components/PlanGate";
import { PageHeader, cardClass, primaryButtonClass } from "@/components/shell/PageHeader";

const API_BASE = import.meta.env.VITE_BASE_API_URL;

const STATUS_META: Record<string, { label: string; tone: string }> = {
  pending: { label: "Waiting for an engineer", tone: "border-warning/25 bg-warning-bg text-warning" },
  assigned: { label: "Engineer assigned", tone: "border-plum/25 bg-selected text-plum" },
  in_progress: { label: "In progress", tone: "border-plum/25 bg-selected text-plum" },
  completed: { label: "Completed", tone: "border-success/25 bg-success-bg text-success" },
  cancelled: { label: "Cancelled", tone: "border-line bg-soft text-muted-foreground" },
};

const ACTIVE_STATUSES = ["pending", "assigned", "in_progress"];

interface ProjectRef {
  id: string;
  name: string;
  type?: string;
}

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

const ExpertHelpPage = () => {
  const token = useAuthToken();
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const { requests, totalAvailableHours, loading } = useEngagement();
  const { isRestricted } = usePlanFeatures();
  const [projects, setProjects] = useState<Record<string, ProjectRef>>({});
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Names and types for the engagement rows (and their workroom links); rows fall back to the id if a project isn't listed
  useEffect(() => {
    if (!token || !API_BASE) return;
    // /protected/projects caps limit at 100; anything higher 422s and every row falls back to the bare id
    const params = new URLSearchParams({ limit: "100", offset: "0" });
    if (activeWorkspace) params.set("workspace_id", String(activeWorkspace.id));
    fetch(`${API_BASE}/protected/projects?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.projects) return;
        setProjects(Object.fromEntries(data.projects.map((p: ProjectRef) => [p.id, { id: p.id, name: p.name || p.id, type: p.type }])));
      })
      .catch(() => { /* rows still render with the project id */ });
  }, [token, activeWorkspace]);

  // Owners never see cancelled engagements elsewhere in the app either
  const visible = useMemo(
    () => requests.filter((r) => r.is_visible_to_owner !== false && r.status !== "cancelled"),
    [requests]
  );

  const items = visible
    .filter((r) => statusFilter === "all" || (statusFilter === "active" ? ACTIVE_STATUSES.includes(r.status) : r.status === statusFilter))
    .filter((r) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [r.project_name, projects[r.project_id]?.name, r.developer_name, r.category, r.request_description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const activeCount = visible.filter((r) => ACTIVE_STATUSES.includes(r.status)).length;
  const completedCount = visible.filter((r) => r.status === "completed").length;

  const openRequest = () => {
    if (isRestricted("engage_expert")) {
      setUpgradeOpen(true);
      return;
    }
    navigate("/expert/request");
  };

  return (
    <div className="mx-auto w-full max-w-[1080px]">
      <PageHeader
        eyebrow={activeWorkspace?.name || "Workspace"}
        title="Expert help"
        description="Track development work, migrations and monthly production cover across your projects."
        actions={
          <PlanGate feature="engage_expert" mode="tooltip" lockPosition="top-right">
            <button type="button" onClick={openRequest} className={primaryButtonClass}>
              <Plus className="h-4 w-4" />
              Request expert help
            </button>
          </PlanGate>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: "Active engagements", value: activeCount, detail: "Work in progress with a Joylo engineer" },
          { label: "Expert hours available", value: totalAvailableHours, detail: "Ready to use on any project" },
          { label: "Completed", value: completedCount, detail: "Finished engagements you can still review" },
        ].map((m) => (
          <div key={m.label} className={`${cardClass} p-5`}>
            <span className="text-[13px] text-muted-foreground">{m.label}</span>
            <strong className="mt-1 block font-display text-[28px] font-semibold tracking-[-0.03em] text-foreground">
              {loading ? "…" : m.value}
            </strong>
            <span className="text-[12px] text-muted-foreground">{m.detail}</span>
          </div>
        ))}
      </div>

      <section className={`${cardClass} mt-4 overflow-hidden`}>
        <div className="flex flex-col gap-3 border-b border-line p-4 sm:flex-row sm:items-center">
          <label className="relative flex-1">
            <span className="sr-only">Search engagements</span>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search project, engagement or engineer"
              className="h-10 w-full rounded-lg border border-input bg-surface pl-9 pr-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-plum/40 focus:outline-none"
            />
          </label>
          <label>
            <span className="sr-only">Filter by status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-lg border border-input bg-surface px-3 text-[14px] text-foreground focus:border-plum/40 focus:outline-none"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Waiting for an engineer</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 text-[12px] text-muted-foreground">
          <span>
            {items.length} {items.length === 1 ? "engagement" : "engagements"}
          </span>
          {(search || statusFilter !== "all") && (
            <button type="button" onClick={() => { setSearch(""); setStatusFilter("all"); }} className="font-semibold text-plum hover:underline">
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 border-t border-line py-14 text-[14px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading engagements…
          </div>
        ) : items.length ? (
          <ul className="divide-y divide-line border-t border-line">
            {items.map((r) => {
              const project = projects[r.project_id];
              // The request carries its own project name, so rows don't depend on the project being in this workspace's list
              const projectName = r.project_name || project?.name || "Project";
              const projectType = project?.type || r.project_type || "fullstack";
              const status = STATUS_META[r.status] ?? STATUS_META.pending;
              const budget = r.total_hours_budget ?? 0;
              const used = r.used_hours ?? 0;
              return (
                <li key={r.request_id}>
                  <Link
                    to={`/projects/${projectType}/${r.project_id}/engagement/chat`}
                    className="group flex flex-col gap-3 px-4 py-4 transition-colors hover:bg-soft sm:flex-row sm:items-center"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-selected text-plum">
                      <ShieldUser className="h-[18px] w-[18px]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-foreground">
                        {projectName}
                        {r.category && <span className="font-normal text-muted-foreground"> · {r.category}</span>}
                      </span>
                      <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">
                        {r.developer_name ? `With ${r.developer_name}` : "An engineer will be assigned soon"} · Requested {formatDate(r.created_at)}
                      </span>
                    </span>
                    {budget > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                        <Clock3 className="h-3.5 w-3.5" />
                        {used} / {budget}h
                      </span>
                    )}
                    <span className={`shrink-0 self-start rounded-full border px-2.5 py-1 text-[12px] font-semibold sm:self-auto ${status.tone}`}>
                      {status.label}
                    </span>
                    <ArrowUpRight className="hidden h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-plum sm:block" />
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="border-t border-line px-6 py-14 text-center">
            <span className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-selected text-plum">
              <ShieldUser className="h-5 w-5" />
            </span>
            <p className="mt-3 text-[15px] font-semibold text-foreground">
              {visible.length ? "No engagements match these filters" : "No expert help yet"}
            </p>
            <p className="mx-auto mt-1 max-w-[420px] text-[13px] text-muted-foreground">
              {visible.length
                ? "Try a different search or status."
                : "Bring in a Joylo engineer for hands-on development, a migration or monthly production cover."}
            </p>
          </div>
        )}
      </section>

      {upgradeOpen && <UpgradeModal feature="engage_expert" isOpen onClose={() => setUpgradeOpen(false)} />}
    </div>
  );
};

export default ExpertHelpPage;
